import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, Users, IndianRupee, TrendingUp, Plus, Search, LogOut, Edit, 
  CreditCard, Receipt, Loader2, GraduationCap, Phone, Mail, MapPin, 
  Calendar, ClipboardList, Bell, ChevronDown, User, Settings, BarChart3,
  ArrowUpRight, ArrowDownRight, Wallet, CheckCircle, AlertTriangle, Clock,
  Eye, MoreVertical, FileText, Target, Zap, DollarSign, UserPlus, CalendarDays,
  TrendingDown, PieChart, Activity, RefreshCw, Headphones
} from 'lucide-react';
import AttendanceManagement from './AttendanceManagement';
import CRMSystem from './CRMSystem';
import AddLeadDialog from '@/components/center/AddLeadDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for center auth
const centerApi = axios.create({
  baseURL: API
});

centerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('center_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const CenterDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState(null);
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Add Student Dialog
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const [studentForm, setStudentForm] = useState({
    name: '', father_name: '', mother_name: '', dob: '', mobile: '', email: '',
    address: '', city: '', district: '', state: '', pin_code: '', qualification: '',
    course_id: '', course_name: '', course_fee: '',
    highest_qualification: '', education_details: []
  });

  // Add Lead Dialog State (using unified AddLeadDialog component)
  const [addLeadOpen, setAddLeadOpen] = useState(false);

  // Fee Payment Dialog
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [feeAmount, setFeeAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [recordingFee, setRecordingFee] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(1); // 1 or 2

  // Commission Payment
  const [payingCommission, setPayingCommission] = useState(false);

  // Student Fees
  const [studentFees, setStudentFees] = useState([]);
  const [cashPaymentOpen, setCashPaymentOpen] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [cashPaymentForm, setCashPaymentForm] = useState({
    installment_number: 1,
    amount: '',
    remarks: ''
  });

  // All available courses - will be fetched from API
  const [allCourses, setAllCourses] = useState([
    { id: 'autocad', name: 'AutoCAD', fee: 12500, category: 'Professional' },
    { id: '3dsmax', name: '3ds Max', fee: 18000, category: 'Professional' },
    { id: 'civilcad', name: 'CIVIL CAD', fee: 25000, category: 'Professional' },
    { id: 'interior', name: 'Interior Architecture', fee: 35000, category: 'Professional' },
    { id: 'digitalmarketing', name: 'Digital Marketing', fee: 12000, category: 'Professional' },
    { id: 'webdesigner', name: 'Web Designer', fee: 20000, category: 'Professional' },
    { id: 'tallygst', name: 'Tally with GST', fee: 10000, category: 'Professional' },
    { id: 'financialaccounting', name: 'Financial Accounting', fee: 18500, category: 'Professional' },
    { id: 'landsurveyor', name: 'Land Surveyor', fee: 18000, category: 'Professional' },
    { id: 'dataentry', name: 'Data Entry Operator', fee: 8000, category: 'Professional' },
    { id: 'safetymanagement', name: 'Safety Management', fee: 15000, category: 'Professional' },
    { id: 'aidatascience', name: 'AI Data Science', fee: 40000, category: 'Professional' },
    { id: 'aidatamanagement', name: 'AI Data Management', fee: 30000, category: 'Professional' },
    { id: 'pgdca', name: 'PGDCA', fee: 26000, category: 'Distance Learning - Diploma' },
    { id: 'ba', name: 'B.A.', fee: 16000, category: 'Distance Learning - UG' },
    { id: 'bcom', name: 'B.Com', fee: 21000, category: 'Distance Learning - UG' },
    { id: 'bca', name: 'BCA', fee: 21500, category: 'Distance Learning - UG' },
    { id: 'bba', name: 'BBA', fee: 21500, category: 'Distance Learning - UG' },
    { id: 'blib', name: 'B.Lib', fee: 25000, category: 'Distance Learning - UG' },
    { id: 'mca', name: 'MCA', fee: 35000, category: 'Distance Learning - PG' },
    { id: 'mba', name: 'MBA', fee: 35000, category: 'Distance Learning - PG' },
    { id: 'mcom', name: 'M.Com (Accountancy)', fee: 21000, category: 'Distance Learning - PG' },
    { id: 'maeconomics', name: 'M.A. (Economics)', fee: 18000, category: 'Distance Learning - PG' },
    { id: 'mscmaths', name: 'M.Sc (Mathematics)', fee: 25000, category: 'Distance Learning - PG' },
  ]);

  // Fetch courses from API
  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`);
      if (res.data && res.data.length > 0) {
        setAllCourses(res.data);
      }
    } catch (error) {
      console.log('Using default courses list');
    }
  };

  // Today's performance stats (calculated from real data)
  const [todayStats, setTodayStats] = useState({
    studentsAdded: 0,
    feesCollected: 0,
    attendancePercent: 0
  });

  // Fetch today's stats
  const fetchTodayStats = async () => {
    try {
      const res = await centerApi.get('/center/today-stats');
      if (res.data) {
        setTodayStats(res.data);
      }
    } catch (error) {
      // Keep default values if API fails
      console.log('Could not fetch today stats');
    }
  };

  // Real-time activity feed
  const [activityFeed, setActivityFeed] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Icon mapping for activity feed
  const activityIconMap = {
    UserPlus: UserPlus,
    IndianRupee: IndianRupee,
    ClipboardList: ClipboardList,
    AlertTriangle: AlertTriangle,
    CreditCard: CreditCard
  };

  // WebSocket connection for real-time updates
  const connectWebSocket = () => {
    const token = localStorage.getItem('center_token');
    if (!token) return;

    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/center?token=${token}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected for center dashboard');
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);
      ws.pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'activity') {
          setActivityFeed(prev => {
            const newActivity = {
              id: `ws_${Date.now()}`,
              type: data.activity_type,
              message: data.message,
              time: data.time,
              icon: data.activity_type === 'fee_payment' ? 'IndianRupee' : 'UserPlus',
              color: data.activity_type === 'fee_payment' ? 'green' : 'blue'
            };
            return [newActivity, ...prev.slice(0, 9)];
          });
          toast.success(data.message, { duration: 3000 });
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected');
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
  };

  // Fetch real-time activity feed
  const fetchActivityFeed = async () => {
    try {
      const res = await centerApi.get('/activity/center');
      setActivityFeed(res.data || []);
    } catch (error) {
      console.error('Failed to fetch activity feed:', error);
      setActivityFeed([]);
    } finally {
      setActivityLoading(false);
    }
  };

  // Process notifications from activity feed
  const notifications = activityFeed.length > 0 ? activityFeed.slice(0, 3).map(a => ({
    id: a.id,
    type: a.type === 'payment' ? 'info' : a.type === 'student' ? 'info' : 'warning',
    message: a.message,
    time: a.time
  })) : [];

  const handleCourseChange = (courseId) => {
    const selectedCourse = allCourses.find(c => c.id === courseId);
    if (selectedCourse) {
      setStudentForm({
        ...studentForm,
        course_id: selectedCourse.id,
        course_name: selectedCourse.name,
        course_fee: selectedCourse.fee.toString()
      });
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('center_token');
    if (!token) {
      navigate('/center/login');
      return;
    }
    fetchData();
    fetchActivityFeed();
    fetchCourses();
    connectWebSocket();
    
    // Auto-refresh activity feed every 30 seconds (fallback)
    const activityInterval = setInterval(fetchActivityFeed, 30000);
    
    return () => {
      clearInterval(activityInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      const [centerRes, statsRes, studentsRes, txRes, feesRes] = await Promise.all([
        centerApi.get('/center/me'),
        centerApi.get('/center/dashboard-stats'),
        centerApi.get('/center/students'),
        centerApi.get('/center/fee-transactions'),
        centerApi.get('/center/student-fees').catch(() => ({ data: [] }))
      ]);
      
      setCenter(centerRes.data);
      setStats(statsRes.data);
      setStudents(studentsRes.data);
      setTransactions(txRes.data);
      setStudentFees(feesRes.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('center_token');
        localStorage.removeItem('center_info');
        navigate('/center/login');
      }
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('center_token');
    localStorage.removeItem('center_info');
    navigate('/auth/login');
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (!studentForm.name || !studentForm.mobile || !studentForm.course_name || !studentForm.course_fee) {
      toast.error('Please fill required fields');
      return;
    }
    
    setAddingStudent(true);
    try {
      await centerApi.post('/center/students', {
        ...studentForm,
        course_fee: parseFloat(studentForm.course_fee)
      });
      toast.success('Student added successfully');
      setAddStudentOpen(false);
      setStudentForm({
        name: '', father_name: '', mother_name: '', dob: '', mobile: '', email: '',
        address: '', city: '', district: '', state: '', pin_code: '', qualification: '',
        course_id: '', course_name: '', course_fee: '',
        highest_qualification: '', education_details: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add student');
    } finally {
      setAddingStudent(false);
    }
  };

  // Add Lead Handler
  // Lead refresh handler for unified AddLeadDialog component
  const handleLeadAdded = () => {
    fetchData(); // Refresh dashboard stats after lead is added
  };

  const handleRecordFee = async () => {
    if (!feeAmount || parseFloat(feeAmount) <= 0) {
      toast.error('Please enter valid amount');
      return;
    }
    
    setRecordingFee(true);
    try {
      const response = await centerApi.post('/center/fee-payment', {
        student_id: selectedStudent.id,
        amount: parseFloat(feeAmount),
        payment_mode: paymentMode,
        installment_number: selectedInstallment
      });
      toast.success(`Installment ${selectedInstallment} payment recorded! Receipt: ${response.data.receipt_number}`);
      setFeeDialogOpen(false);
      setSelectedStudent(null);
      setFeeAmount('');
      setSelectedInstallment(1);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setRecordingFee(false);
    }
  };

  const handlePayCommission = async () => {
    if (stats?.iqraskill_pending <= 0) {
      toast.info('No pending commission to pay');
      return;
    }
    
    setPayingCommission(true);
    try {
      const orderRes = await centerApi.post('/center/commission/create-order');
      const { order_id, amount, key_id } = orderRes.data;
      
      const options = {
        key: key_id,
        amount: amount * 100,
        currency: 'INR',
        name: 'IqraSkill Commission',
        description: 'Commission Payment to IqraSkill',
        order_id: order_id,
        handler: async function (response) {
          try {
            await centerApi.post('/center/commission/verify-payment', null, {
              params: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              }
            });
            toast.success('Commission payment successful!');
            fetchData();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: center?.owner_name,
          email: center?.email,
          contact: center?.mobile
        },
        theme: { color: '#2563eb' }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPayingCommission(false);
    }
  };

  const handleRecordCashPayment = async (e) => {
    e.preventDefault();
    if (!selectedFee || !cashPaymentForm.amount) return;
    
    setRecordingFee(true);
    try {
      await centerApi.post('/center/record-cash-payment', {
        student_fee_id: selectedFee.id,
        installment_number: cashPaymentForm.installment_number,
        amount: parseFloat(cashPaymentForm.amount),
        remarks: cashPaymentForm.remarks
      });
      toast.success('Cash payment recorded successfully!');
      setCashPaymentOpen(false);
      setSelectedFee(null);
      setCashPaymentForm({ installment_number: 1, amount: '', remarks: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setRecordingFee(false);
    }
  };

  const openCashPaymentDialog = (fee, installmentNumber) => {
    setSelectedFee(fee);
    const amount = installmentNumber === 1 ? fee.installment_1_amount : fee.installment_2_amount;
    setCashPaymentForm({
      installment_number: installmentNumber,
      amount: amount.toString(),
      remarks: ''
    });
    setCashPaymentOpen(true);
  };

  // Open fee dialog with auto-calculated installments
  const openFeeDialog = (student) => {
    setSelectedStudent(student);
    // Auto-select pending installment
    if (student.installment_1_status !== 'paid') {
      setSelectedInstallment(1);
      setFeeAmount(student.installment_1_amount?.toString() || '');
    } else if (student.installment_2_status !== 'paid') {
      setSelectedInstallment(2);
      setFeeAmount(student.installment_2_amount?.toString() || '');
    } else {
      setSelectedInstallment(1);
      setFeeAmount('');
    }
    setFeeDialogOpen(true);
  };

  // Handle installment selection change
  const handleInstallmentChange = (installmentNum) => {
    setSelectedInstallment(installmentNum);
    if (selectedStudent) {
      if (installmentNum === 1) {
        const remaining = (selectedStudent.installment_1_amount || 0) - (selectedStudent.installment_1_paid_amount || 0);
        setFeeAmount(remaining > 0 ? remaining.toString() : '');
      } else {
        const remaining = (selectedStudent.installment_2_amount || 0) - (selectedStudent.installment_2_paid_amount || 0);
        setFeeAmount(remaining > 0 ? remaining.toString() : '');
      }
    }
  };

  const filteredStudents = students.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.mobile?.includes(searchTerm) ||
    s.course_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate alerts
  const pendingFeeStudents = students.filter(s => s.fee_pending > 0).length;
  const alerts = [
    ...(pendingFeeStudents > 0 ? [{ type: 'warning', message: `${pendingFeeStudents} students have pending fees`, icon: AlertTriangle }] : []),
    ...(stats?.iqraskill_pending > 0 ? [{ type: 'danger', message: `Commission due: ₹${stats.iqraskill_pending.toLocaleString()}`, icon: CreditCard }] : [])
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Modern Top Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm" data-testid="dashboard-header">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo & Center Info */}
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/4n9x2udq_1%20copy.png" 
                  alt="Iqra Skills Logo" 
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="hidden sm:block">
                  <h1 className="font-bold text-slate-800 leading-tight">{center?.center_name}</h1>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                    {center?.center_code}
                  </span>
                </div>
              </Link>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              {/* Add Student Button */}
              <Dialog open={addStudentOpen} onOpenChange={setAddStudentOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 hidden sm:flex" data-testid="add-student-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Student
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-blue-600" />
                      Add New Student
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddStudent} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Student Name *</Label>
                        <Input
                          value={studentForm.name}
                          onChange={(e) => setStudentForm({...studentForm, name: e.target.value})}
                          placeholder="Full name"
                        />
                      </div>
                      <div>
                        <Label>Mobile *</Label>
                        <Input
                          value={studentForm.mobile}
                          onChange={(e) => setStudentForm({...studentForm, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                          placeholder="10-digit mobile"
                        />
                      </div>
                      <div>
                        <Label>Father's Name</Label>
                        <Input
                          value={studentForm.father_name}
                          onChange={(e) => setStudentForm({...studentForm, father_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Mother's Name</Label>
                        <Input
                          value={studentForm.mother_name}
                          onChange={(e) => setStudentForm({...studentForm, mother_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Date of Birth</Label>
                        <Input
                          type="date"
                          value={studentForm.dob}
                          onChange={(e) => setStudentForm({...studentForm, dob: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={studentForm.email}
                          onChange={(e) => setStudentForm({...studentForm, email: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Qualification (योग्यता) *</Label>
                        <select
                          value={studentForm.highest_qualification}
                          onChange={(e) => setStudentForm({...studentForm, highest_qualification: e.target.value})}
                          className="w-full h-10 px-3 border rounded-lg bg-white text-sm"
                          required
                        >
                          <option value="">-- Select Qualification --</option>
                          <option value="10th">10th Pass (High School)</option>
                          <option value="12th">12th Pass (Intermediate)</option>
                          <option value="graduation">Graduation</option>
                          <option value="post_graduation">Post Graduation</option>
                        </select>
                      </div>
                      <div>
                        <Label>City</Label>
                        <Input
                          value={studentForm.city}
                          onChange={(e) => setStudentForm({...studentForm, city: e.target.value})}
                        />
                      </div>

                      {/* Education Details Section */}
                      {studentForm.highest_qualification && (
                        <div className="col-span-2 bg-slate-50 rounded-xl p-4 border border-slate-200">
                          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <GraduationCap className="w-4 h-4 text-blue-600" />
                            Education Details (शैक्षिक विवरण)
                          </h3>
                          
                          {/* 10th Details */}
                          <div className="grid grid-cols-4 gap-3 mb-3">
                            <div className="col-span-4 text-sm font-medium text-slate-600 bg-white px-2 py-1 rounded border">
                              10th (High School)
                            </div>
                            <div>
                              <Label className="text-xs">Board</Label>
                              <Input
                                placeholder="CBSE/RBSE"
                                className="h-8 text-sm"
                                value={studentForm.education_details?.find(e => e.level === '10th')?.board || ''}
                                onChange={(e) => {
                                  const existing = studentForm.education_details?.filter(ed => ed.level !== '10th') || [];
                                  const current = studentForm.education_details?.find(ed => ed.level === '10th') || { level: '10th' };
                                  setStudentForm({
                                    ...studentForm,
                                    education_details: [...existing, { ...current, board: e.target.value }]
                                  });
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Year</Label>
                              <Input
                                type="number"
                                placeholder="2020"
                                className="h-8 text-sm"
                                value={studentForm.education_details?.find(e => e.level === '10th')?.year || ''}
                                onChange={(e) => {
                                  const existing = studentForm.education_details?.filter(ed => ed.level !== '10th') || [];
                                  const current = studentForm.education_details?.find(ed => ed.level === '10th') || { level: '10th' };
                                  setStudentForm({
                                    ...studentForm,
                                    education_details: [...existing, { ...current, year: e.target.value }]
                                  });
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Percentage</Label>
                              <Input
                                type="number"
                                placeholder="75"
                                className="h-8 text-sm"
                                value={studentForm.education_details?.find(e => e.level === '10th')?.percentage || ''}
                                onChange={(e) => {
                                  const existing = studentForm.education_details?.filter(ed => ed.level !== '10th') || [];
                                  const current = studentForm.education_details?.find(ed => ed.level === '10th') || { level: '10th' };
                                  setStudentForm({
                                    ...studentForm,
                                    education_details: [...existing, { ...current, percentage: e.target.value }]
                                  });
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Total Marks</Label>
                              <Input
                                type="number"
                                placeholder="500"
                                className="h-8 text-sm"
                                value={studentForm.education_details?.find(e => e.level === '10th')?.total_marks || ''}
                                onChange={(e) => {
                                  const existing = studentForm.education_details?.filter(ed => ed.level !== '10th') || [];
                                  const current = studentForm.education_details?.find(ed => ed.level === '10th') || { level: '10th' };
                                  setStudentForm({
                                    ...studentForm,
                                    education_details: [...existing, { ...current, total_marks: e.target.value }]
                                  });
                                }}
                              />
                            </div>
                          </div>

                          {/* 12th Details - Show if 12th or higher */}
                          {['12th', 'graduation', 'post_graduation'].includes(studentForm.highest_qualification) && (
                            <div className="grid grid-cols-4 gap-3 mb-3">
                              <div className="col-span-4 text-sm font-medium text-slate-600 bg-white px-2 py-1 rounded border">
                                12th (Intermediate)
                              </div>
                              <div>
                                <Label className="text-xs">Board</Label>
                                <Input
                                  placeholder="CBSE/RBSE"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === '12th')?.board || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== '12th') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === '12th') || { level: '12th' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, board: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Year</Label>
                                <Input
                                  type="number"
                                  placeholder="2022"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === '12th')?.year || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== '12th') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === '12th') || { level: '12th' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, year: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Percentage</Label>
                                <Input
                                  type="number"
                                  placeholder="80"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === '12th')?.percentage || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== '12th') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === '12th') || { level: '12th' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, percentage: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Total Marks</Label>
                                <Input
                                  type="number"
                                  placeholder="500"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === '12th')?.total_marks || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== '12th') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === '12th') || { level: '12th' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, total_marks: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          {/* Graduation Details - Show if graduation or higher */}
                          {['graduation', 'post_graduation'].includes(studentForm.highest_qualification) && (
                            <div className="grid grid-cols-4 gap-3 mb-3">
                              <div className="col-span-4 text-sm font-medium text-slate-600 bg-white px-2 py-1 rounded border">
                                Graduation
                              </div>
                              <div>
                                <Label className="text-xs">University</Label>
                                <Input
                                  placeholder="University name"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === 'graduation')?.university || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== 'graduation') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === 'graduation') || { level: 'graduation' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, university: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Year</Label>
                                <Input
                                  type="number"
                                  placeholder="2024"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === 'graduation')?.year || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== 'graduation') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === 'graduation') || { level: 'graduation' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, year: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Percentage</Label>
                                <Input
                                  type="number"
                                  placeholder="70"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === 'graduation')?.percentage || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== 'graduation') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === 'graduation') || { level: 'graduation' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, percentage: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Degree</Label>
                                <Input
                                  placeholder="B.A/B.Sc/BCA"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === 'graduation')?.degree || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== 'graduation') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === 'graduation') || { level: 'graduation' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, degree: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          {/* Post Graduation Details */}
                          {studentForm.highest_qualification === 'post_graduation' && (
                            <div className="grid grid-cols-4 gap-3">
                              <div className="col-span-4 text-sm font-medium text-slate-600 bg-white px-2 py-1 rounded border">
                                Post Graduation
                              </div>
                              <div>
                                <Label className="text-xs">University</Label>
                                <Input
                                  placeholder="University name"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === 'post_graduation')?.university || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== 'post_graduation') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === 'post_graduation') || { level: 'post_graduation' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, university: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Year</Label>
                                <Input
                                  type="number"
                                  placeholder="2026"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === 'post_graduation')?.year || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== 'post_graduation') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === 'post_graduation') || { level: 'post_graduation' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, year: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Percentage</Label>
                                <Input
                                  type="number"
                                  placeholder="65"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === 'post_graduation')?.percentage || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== 'post_graduation') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === 'post_graduation') || { level: 'post_graduation' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, percentage: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Degree</Label>
                                <Input
                                  placeholder="M.A/M.Sc/MCA"
                                  className="h-8 text-sm"
                                  value={studentForm.education_details?.find(e => e.level === 'post_graduation')?.degree || ''}
                                  onChange={(e) => {
                                    const existing = studentForm.education_details?.filter(ed => ed.level !== 'post_graduation') || [];
                                    const current = studentForm.education_details?.find(ed => ed.level === 'post_graduation') || { level: 'post_graduation' };
                                    setStudentForm({
                                      ...studentForm,
                                      education_details: [...existing, { ...current, degree: e.target.value }]
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="col-span-2">
                        <Label>Course Name *</Label>
                        <select
                          value={studentForm.course_id}
                          onChange={(e) => handleCourseChange(e.target.value)}
                          className="w-full h-10 px-3 border rounded-lg bg-white text-sm"
                          required
                        >
                          <option value="">-- Select Course --</option>
                          <optgroup label="Professional Courses">
                            {allCourses.filter(c => c.category === 'Professional').map(course => (
                              <option key={course.id} value={course.id}>
                                {course.name} - ₹{course.fee.toLocaleString()}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="Distance Learning - Diploma">
                            {allCourses.filter(c => c.category === 'Distance Learning - Diploma').map(course => (
                              <option key={course.id} value={course.id}>
                                {course.name} - ₹{course.fee.toLocaleString()}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="Distance Learning - UG Degree">
                            {allCourses.filter(c => c.category === 'Distance Learning - UG').map(course => (
                              <option key={course.id} value={course.id}>
                                {course.name} - ₹{course.fee.toLocaleString()}/year
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="Distance Learning - PG Degree">
                            {allCourses.filter(c => c.category === 'Distance Learning - PG').map(course => (
                              <option key={course.id} value={course.id}>
                                {course.name} - ₹{course.fee.toLocaleString()}/year
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                      <div>
                        <Label>Course Fee (₹) *</Label>
                        <Input
                          type="number"
                          value={studentForm.course_fee}
                          onChange={(e) => setStudentForm({...studentForm, course_fee: e.target.value})}
                          placeholder="Auto-filled from course"
                          readOnly
                          className="bg-slate-50"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setAddStudentOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={addingStudent} className="bg-blue-600 hover:bg-blue-700">
                        {addingStudent ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add Student
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Add Lead Button - Using Unified AddLeadDialog Component */}
              <Button 
                variant="outline" 
                className="border-emerald-200 text-emerald-600 hover:bg-emerald-50 hidden sm:flex" 
                data-testid="add-lead-btn"
                onClick={() => setAddLeadOpen(true)}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
              <AddLeadDialog 
                open={addLeadOpen} 
                onOpenChange={setAddLeadOpen}
                onSuccess={handleLeadAdded}
              />

              {/* Notification Bell */}
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  data-testid="notification-bell"
                >
                  <Bell className="w-5 h-5 text-slate-600" />
                  {alerts.length > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-100">
                      <h3 className="font-semibold text-slate-800">Notifications</h3>
                    </div>
                    {notifications.map(notif => (
                      <div key={notif.id} className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50">
                        <p className="text-sm text-slate-700">{notif.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{notif.time}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Profile Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  data-testid="profile-menu"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {center?.owner_name?.charAt(0) || 'C'}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:block" />
                </button>
                
                {/* Profile Dropdown Menu */}
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="font-medium text-slate-800">{center?.owner_name}</p>
                      <p className="text-sm text-slate-500">{center?.email}</p>
                    </div>
                    <button 
                      onClick={() => { setActiveTab('profile'); setShowProfileMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <User className="w-4 h-4" /> Profile
                    </button>
                    <button 
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" /> Settings
                    </button>
                    <hr className="my-2" />
                    <button 
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        
        {/* KPI Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="kpi-cards">
          {/* Total Students */}
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Total Students</p>
                <p className="text-3xl font-bold text-slate-800">{stats?.total_students || 0}</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-emerald-600 font-medium">+12% this month</span>
                </div>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-slate-800">₹{((stats?.total_revenue || 0) / 1000).toFixed(0)}K</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-emerald-600 font-medium">+8% this month</span>
                </div>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors">
                <IndianRupee className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Your Earnings */}
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all cursor-pointer text-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-100 mb-1">Your Earnings (60%)</p>
                <p className="text-3xl font-bold">₹{((stats?.center_share || 0) / 1000).toFixed(0)}K</p>
                <div className="flex items-center gap-1 mt-2">
                  <TrendingUp className="w-4 h-4 text-emerald-200" />
                  <span className="text-xs text-emerald-100 font-medium">Keep earning!</span>
                </div>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Wallet className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Iqra Share / Due */}
          <div className={`rounded-2xl p-5 shadow-sm hover:shadow-md transition-all cursor-pointer ${
            stats?.iqraskill_pending > 0 
              ? 'bg-gradient-to-br from-red-500 to-red-600 text-white' 
              : 'bg-white border border-slate-200'
          }`}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-sm font-medium mb-1 ${stats?.iqraskill_pending > 0 ? 'text-red-100' : 'text-slate-500'}`}>
                  Iqra Skills Due
                </p>
                <p className={`text-3xl font-bold ${stats?.iqraskill_pending > 0 ? 'text-white' : 'text-slate-800'}`}>
                  ₹{((stats?.iqraskill_pending || 0) / 1000).toFixed(0)}K
                </p>
                {stats?.iqraskill_pending > 0 && (
                  <button 
                    onClick={handlePayCommission}
                    className="mt-2 text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full font-medium transition-colors"
                  >
                    Pay Now →
                  </button>
                )}
              </div>
              <div className={`p-3 rounded-xl ${stats?.iqraskill_pending > 0 ? 'bg-white/20' : 'bg-amber-100'}`}>
                <CreditCard className={`w-6 h-6 ${stats?.iqraskill_pending > 0 ? 'text-white' : 'text-amber-600'}`} />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions & Today's Performance */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* Quick Actions */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="quick-actions">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button 
                onClick={() => setAddStudentOpen(true)}
                className="flex flex-col items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all group"
              >
                <div className="p-3 bg-blue-500 rounded-xl group-hover:scale-110 transition-transform">
                  <UserPlus className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-slate-700">Add Student</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('fee-collection')}
                className="flex flex-col items-center gap-3 p-4 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-all group"
              >
                <div className="p-3 bg-emerald-500 rounded-xl group-hover:scale-110 transition-transform">
                  <IndianRupee className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-slate-700">Collect Fees</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('attendance')}
                className="flex flex-col items-center gap-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-xl transition-all group"
              >
                <div className="p-3 bg-purple-500 rounded-xl group-hover:scale-110 transition-transform">
                  <CalendarDays className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-slate-700">Mark Attendance</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('transactions')}
                className="flex flex-col items-center gap-3 p-4 bg-amber-50 hover:bg-amber-100 rounded-xl transition-all group"
              >
                <div className="p-3 bg-amber-500 rounded-xl group-hover:scale-110 transition-transform">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-slate-700">View Reports</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('crm')}
                className="flex flex-col items-center gap-3 p-4 bg-cyan-50 hover:bg-cyan-100 rounded-xl transition-all group col-span-2 md:col-span-4"
              >
                <div className="p-3 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl group-hover:scale-110 transition-transform">
                  <Headphones className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-slate-700">Open CRM - Manage Leads & Enquiries</span>
              </button>
            </div>
          </div>

          {/* Today's Performance */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="today-performance">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-500" />
                Recent Activity
              </h3>
              <button onClick={fetchActivityFeed} className="text-blue-500 hover:text-blue-600 transition-colors">
                <RefreshCw className={`w-4 h-4 ${activityLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <div className="space-y-4">
              {activityLoading ? (
                <div className="text-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto" />
                  <p className="text-xs text-slate-500 mt-2">Loading...</p>
                </div>
              ) : activityFeed.length === 0 ? (
                <>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <UserPlus className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-sm text-slate-600">Students Added</span>
                    </div>
                    <span className="font-bold text-slate-800">{todayStats.studentsAdded}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <IndianRupee className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="text-sm text-slate-600">Fees Collected</span>
                    </div>
                    <span className="font-bold text-emerald-600">₹{todayStats.feesCollected.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Target className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="text-sm text-slate-600">Attendance</span>
                    </div>
                    <span className="font-bold text-purple-600">{todayStats.attendancePercent}%</span>
                  </div>
                </>
              ) : (
                activityFeed.slice(0, 5).map(activity => {
                  const IconComponent = activityIconMap[activity.icon] || Activity;
                  return (
                    <div key={activity.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          activity.color === 'blue' ? 'bg-blue-100' :
                          activity.color === 'green' ? 'bg-emerald-100' :
                          activity.color === 'purple' ? 'bg-purple-100' :
                          'bg-slate-100'
                        }`}>
                          <IconComponent className={`w-4 h-4 ${
                            activity.color === 'blue' ? 'text-blue-600' :
                            activity.color === 'green' ? 'text-emerald-600' :
                            activity.color === 'purple' ? 'text-purple-600' :
                            'text-slate-600'
                          }`} />
                        </div>
                        <div>
                          <span className="text-sm text-slate-700">{activity.message}</span>
                          <p className="text-xs text-slate-400">{activity.time}</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Operations Flow */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm" data-testid="operations-flow">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-500" />
            Center Operations Flow
          </h3>
          <div className="flex flex-wrap items-center justify-between gap-4">
            {[
              { step: 1, title: 'Enroll Students', icon: GraduationCap, color: 'emerald', action: () => setAddStudentOpen(true) },
              { step: 2, title: 'Collect Fees', icon: IndianRupee, color: 'amber', action: () => setActiveTab('fee-collection') },
              { step: 3, title: 'Track Attendance', icon: ClipboardList, color: 'purple', action: () => setActiveTab('attendance') },
              { step: 4, title: 'Earn Revenue', icon: TrendingUp, color: 'blue', action: () => setActiveTab('transactions') }
            ].map((item, index) => (
              <React.Fragment key={item.step}>
                <button 
                  onClick={item.action}
                  className={`flex items-center gap-3 px-4 py-3 bg-${item.color}-50 hover:bg-${item.color}-100 rounded-xl border border-${item.color}-200 transition-all group cursor-pointer flex-1 min-w-[180px]`}
                >
                  <div className={`w-8 h-8 bg-${item.color}-500 rounded-full flex items-center justify-center text-white font-bold text-sm group-hover:scale-110 transition-transform`}>
                    {item.step}
                  </div>
                  <div className="text-left">
                    <p className={`text-sm font-medium text-${item.color}-700`}>{item.title}</p>
                  </div>
                  <item.icon className={`w-5 h-5 text-${item.color}-500 ml-auto`} />
                </button>
                {index < 3 && (
                  <div className="hidden lg:block text-slate-300 text-2xl">→</div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Alerts & Notifications */}
        {alerts.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm" data-testid="alerts-panel">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-500" />
              Alerts & Notifications
            </h3>
            <div className="space-y-3">
              {alerts.map((alert, index) => (
                <div 
                  key={index}
                  className={`flex items-center gap-3 p-4 rounded-xl ${
                    alert.type === 'danger' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
                  }`}
                >
                  <alert.icon className={`w-5 h-5 ${alert.type === 'danger' ? 'text-red-500' : 'text-amber-500'}`} />
                  <span className={`text-sm font-medium ${alert.type === 'danger' ? 'text-red-700' : 'text-amber-700'}`}>
                    {alert.message}
                  </span>
                  {alert.type === 'danger' && (
                    <Button 
                      size="sm" 
                      onClick={handlePayCommission}
                      className="ml-auto bg-red-500 hover:bg-red-600 text-white"
                    >
                      Pay Now
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 mb-6 p-1 rounded-xl flex-wrap">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Overview
            </TabsTrigger>
            <TabsTrigger value="crm" className="rounded-lg data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <Headphones className="w-4 h-4 mr-1" /> CRM
            </TabsTrigger>
            <TabsTrigger value="students" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Students
            </TabsTrigger>
            <TabsTrigger value="fee-collection" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Fee Collection
            </TabsTrigger>
            <TabsTrigger value="attendance" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Attendance
            </TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Transactions
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Profile
            </TabsTrigger>
          </TabsList>

          {/* CRM Tab */}
          <TabsContent value="crm">
            <CRMSystem courses={allCourses} />
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Students */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="recent-students">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800">Recent Students</h3>
                  <Button size="sm" variant="outline" onClick={() => setActiveTab('students')} className="rounded-lg">
                    View All
                  </Button>
                </div>
                <div className="space-y-3">
                  {students.slice(0, 5).map(student => (
                    <div key={student.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                          {student.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{student.name}</p>
                          <p className="text-sm text-slate-500">{student.course_name}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                        student.fee_pending > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {student.fee_pending > 0 ? `₹${student.fee_pending.toLocaleString()} Due` : 'Paid'}
                      </span>
                    </div>
                  ))}
                  {students.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No students yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Revenue Summary */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-purple-500" />
                  Revenue Summary
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600">Total Fee Collected</span>
                    <span className="font-bold text-slate-800">₹{(stats?.total_revenue || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-xl">
                    <span className="text-amber-700">Pending Fees</span>
                    <span className="font-bold text-amber-600">₹{(stats?.pending_fees || 0).toLocaleString()}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600">Iqra Skills Share ({stats?.commission_rate}%)</span>
                    <span className="font-bold">₹{(stats?.iqraskill_share || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-xl">
                    <span className="text-emerald-700">Commission Paid</span>
                    <span className="font-bold text-emerald-600">₹{(stats?.iqraskill_paid || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-xl">
                    <span className="text-red-700">Commission Pending</span>
                    <span className="font-bold text-red-600">₹{(stats?.iqraskill_pending || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row gap-4 justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search students by name, mobile or course..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 rounded-xl"
                  />
                </div>
                <Button onClick={() => setAddStudentOpen(true)} className="bg-blue-600 hover:bg-blue-700 rounded-xl">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Student
                </Button>
              </div>

              {/* Responsive Table Container with Custom Scrollbar */}
              <div 
                className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cbd5e1 #f1f5f9'
                }}
                data-testid="students-table-container"
              >
                <table className="w-full min-w-[900px]">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap min-w-[200px]">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap min-w-[180px]">Course</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap min-w-[160px]">1st Installment (60%)</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap min-w-[160px]">2nd Installment (40%)</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700 whitespace-nowrap min-w-[180px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredStudents.map(student => (
                      <tr key={student.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                              {student.name?.charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate max-w-[150px]">{student.name}</p>
                              <p className="text-sm text-slate-500">{student.mobile}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <p className="text-slate-800 truncate max-w-[150px]">{student.course_name}</p>
                          <p className="text-sm text-slate-500">Total: ₹{student.course_fee?.toLocaleString()}</p>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-emerald-700">
                              ₹{(student.installment_1_amount || Math.round(student.course_fee * 0.6))?.toLocaleString()}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded inline-block w-fit ${
                              student.installment_1_status === 'paid' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {student.installment_1_status === 'paid' ? 'PAID' : 'PENDING'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-amber-700">
                              ₹{(student.installment_2_amount || Math.round(student.course_fee * 0.4))?.toLocaleString()}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded inline-block w-fit ${
                              student.installment_2_status === 'paid' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {student.installment_2_status === 'paid' ? 'PAID' : 'PENDING'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-lg"
                              onClick={() => openFeeDialog(student)}
                            >
                              <Receipt className="w-4 h-4 mr-1" />
                              Record Fee
                            </Button>
                            <Button size="sm" variant="ghost" className="rounded-lg">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredStudents.length === 0 && (
                  <div className="text-center py-12 text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No students found</p>
                  </div>
                )}
              </div>
              
              {/* Mobile Scroll Hint */}
              <div className="lg:hidden px-4 py-2 text-center text-xs text-slate-400 border-t border-slate-100">
                ← Scroll horizontally to view all columns →
              </div>
            </div>

            {/* Fee Payment Dialog - Enhanced with Installments */}
            <Dialog open={feeDialogOpen} onOpenChange={setFeeDialogOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-emerald-600" />
                    Record Fee Payment
                  </DialogTitle>
                </DialogHeader>
                {selectedStudent && (
                  <div className="space-y-4 mt-4">
                    {/* Student Info */}
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <p className="font-medium text-slate-800">{selectedStudent.name}</p>
                      <p className="text-sm text-slate-600">{selectedStudent.course_name}</p>
                      <p className="text-sm mt-1">Total Fee: ₹{selectedStudent.course_fee?.toLocaleString()}</p>
                    </div>
                    
                    {/* Installment Breakdown */}
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                      <p className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                        <CreditCard className="w-4 h-4" /> Installment Breakdown (Auto-Divided)
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {/* Installment 1 */}
                        <div 
                          onClick={() => handleInstallmentChange(1)}
                          className={`p-3 rounded-lg cursor-pointer transition-all ${
                            selectedInstallment === 1 
                              ? 'bg-emerald-100 border-2 border-emerald-500' 
                              : 'bg-white border border-slate-200 hover:border-emerald-300'
                          } ${selectedStudent.installment_1_status === 'paid' ? 'opacity-60' : ''}`}
                        >
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium text-slate-700">1st Installment (60%)</span>
                            {selectedStudent.installment_1_status === 'paid' && (
                              <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded">PAID</span>
                            )}
                          </div>
                          <p className="text-lg font-bold text-emerald-600">
                            ₹{selectedStudent.installment_1_amount?.toLocaleString() || Math.round(selectedStudent.course_fee * 0.6).toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Paid: ₹{selectedStudent.installment_1_paid_amount?.toLocaleString() || 0}
                          </p>
                        </div>
                        
                        {/* Installment 2 */}
                        <div 
                          onClick={() => handleInstallmentChange(2)}
                          className={`p-3 rounded-lg cursor-pointer transition-all ${
                            selectedInstallment === 2 
                              ? 'bg-emerald-100 border-2 border-emerald-500' 
                              : 'bg-white border border-slate-200 hover:border-emerald-300'
                          } ${selectedStudent.installment_2_status === 'paid' ? 'opacity-60' : ''}`}
                        >
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium text-slate-700">2nd Installment (40%)</span>
                            {selectedStudent.installment_2_status === 'paid' && (
                              <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded">PAID</span>
                            )}
                          </div>
                          <p className="text-lg font-bold text-amber-600">
                            ₹{selectedStudent.installment_2_amount?.toLocaleString() || Math.round(selectedStudent.course_fee * 0.4).toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Paid: ₹{selectedStudent.installment_2_paid_amount?.toLocaleString() || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Selected Installment Info */}
                    <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                      <p className="text-sm text-emerald-700">
                        Recording payment for <strong>Installment #{selectedInstallment}</strong>
                      </p>
                    </div>
                    
                    {/* Amount Input */}
                    <div>
                      <Label>Amount (₹)</Label>
                      <Input
                        type="number"
                        value={feeAmount}
                        onChange={(e) => setFeeAmount(e.target.value)}
                        placeholder="Enter amount"
                        className="rounded-xl text-lg"
                      />
                    </div>
                    
                    {/* Payment Mode */}
                    <div>
                      <Label>Payment Mode</Label>
                      <select
                        value={paymentMode}
                        onChange={(e) => setPaymentMode(e.target.value)}
                        className="w-full h-10 px-3 border rounded-xl bg-white"
                      >
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="online">Online Transfer</option>
                      </select>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={() => setFeeDialogOpen(false)} className="rounded-xl">Cancel</Button>
                      <Button onClick={handleRecordFee} disabled={recordingFee} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                        {recordingFee ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                        Record Installment {selectedInstallment}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Fee Collection Tab */}
          <TabsContent value="fee-collection">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <IndianRupee className="h-5 w-5 text-amber-600" /> Fee Collection - Record Cash Payments
              </h3>
              
              {studentFees.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No fee records found for your center students yet.</p>
                </div>
              ) : (
                <div 
                  className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e1 #f1f5f9'
                  }}
                  data-testid="fee-collection-table-container"
                >
                  <table className="w-full text-sm min-w-[900px]">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 rounded-l-lg whitespace-nowrap min-w-[180px]">Student</th>
                        <th className="text-left p-3 whitespace-nowrap min-w-[120px]">Course</th>
                        <th className="text-left p-3 whitespace-nowrap min-w-[100px]">Net Fee</th>
                        <th className="text-left p-3 whitespace-nowrap min-w-[180px]">1st Installment (60%)</th>
                        <th className="text-left p-3 whitespace-nowrap min-w-[180px]">2nd Installment (40%)</th>
                        <th className="text-left p-3 rounded-r-lg whitespace-nowrap min-w-[100px]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentFees.map(fee => (
                        <tr key={fee.id} className="border-t hover:bg-slate-50">
                          <td className="p-3 whitespace-nowrap">
                            <p className="font-medium">{fee.student_name}</p>
                            <p className="text-xs text-slate-400">{fee.student_email}</p>
                          </td>
                          <td className="p-3 whitespace-nowrap">{fee.course_name}</td>
                          <td className="p-3 font-medium whitespace-nowrap">₹{fee.net_fee?.toLocaleString()}</td>
                          <td className="p-3 whitespace-nowrap">
                            <div className="space-y-1">
                              <p>₹{fee.installment_1_amount?.toLocaleString()}</p>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                fee.installment_1_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                fee.installment_1_status === 'overdue' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {fee.installment_1_status}
                              </span>
                              {fee.installment_1_status !== 'paid' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="ml-2 text-xs rounded-lg"
                                  onClick={() => openCashPaymentDialog(fee, 1)}
                                >
                                  Record Cash
                                </Button>
                              )}
                            </div>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <div className="space-y-1">
                              <p>₹{fee.installment_2_amount?.toLocaleString()}</p>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                fee.installment_2_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                fee.installment_2_status === 'overdue' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {fee.installment_2_status}
                              </span>
                              {fee.installment_2_status !== 'paid' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="ml-2 text-xs rounded-lg"
                                  onClick={() => openCashPaymentDialog(fee, 2)}
                                >
                                  Record Cash
                                </Button>
                              )}
                            </div>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              fee.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                              fee.status === 'partial' ? 'bg-blue-100 text-blue-700' :
                              fee.status === 'overdue' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {fee.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Cash Payment Dialog */}
            <Dialog open={cashPaymentOpen} onOpenChange={setCashPaymentOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Record Cash Payment</DialogTitle>
                </DialogHeader>
                {selectedFee && (
                  <form onSubmit={handleRecordCashPayment} className="space-y-4">
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <p className="font-medium">{selectedFee.student_name}</p>
                      <p className="text-sm text-slate-600">{selectedFee.course_name}</p>
                      <p className="text-sm text-slate-500 mt-1">Installment #{cashPaymentForm.installment_number}</p>
                    </div>
                    <div>
                      <Label>Amount (₹)</Label>
                      <Input
                        type="number"
                        value={cashPaymentForm.amount}
                        onChange={(e) => setCashPaymentForm({...cashPaymentForm, amount: e.target.value})}
                        required
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <Label>Remarks (Optional)</Label>
                      <Input
                        value={cashPaymentForm.remarks}
                        onChange={(e) => setCashPaymentForm({...cashPaymentForm, remarks: e.target.value})}
                        placeholder="Any notes..."
                        className="rounded-xl"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl" disabled={recordingFee}>
                      {recordingFee ? 'Recording...' : 'Record Payment'}
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance">
            <AttendanceManagement 
              centerApi={centerApi} 
              courses={allCourses}
              students={students}
            />
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-blue-600" />
                  Fee Transactions
                </h3>
              </div>
              {/* Responsive Transaction Table */}
              <div 
                className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cbd5e1 #f1f5f9'
                }}
                data-testid="transactions-table-container"
              >
                <table className="w-full min-w-[700px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap min-w-[120px]">Receipt #</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap min-w-[150px]">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap min-w-[100px]">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap min-w-[100px]">Mode</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap min-w-[120px]">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {transactions.map(tx => (
                      <tr key={tx.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 font-mono text-sm text-blue-600 whitespace-nowrap">{tx.receipt_number}</td>
                        <td className="px-4 py-3 text-slate-800 whitespace-nowrap">{tx.student_name}</td>
                        <td className="px-4 py-3 font-medium text-emerald-600 whitespace-nowrap">₹{tx.amount?.toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 bg-slate-100 rounded-full text-xs text-slate-600 capitalize">
                            {tx.payment_mode}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {tx.payment_date ? new Date(tx.payment_date).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {transactions.length === 0 && (
                  <div className="text-center py-12 text-slate-400">
                    <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No transactions yet</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Center Profile
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <Label className="text-slate-500 text-xs">Center Code</Label>
                    <p className="font-mono font-bold text-blue-600 text-lg">{center?.center_code}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <Label className="text-slate-500 text-xs">Center Name</Label>
                    <p className="font-medium text-slate-800">{center?.center_name}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <Label className="text-slate-500 text-xs">Owner Name</Label>
                    <p className="text-slate-800">{center?.owner_name}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <Label className="text-slate-500 text-xs">Email</Label>
                    <p className="text-slate-800">{center?.email}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <Label className="text-slate-500 text-xs">Mobile</Label>
                    <p className="text-slate-800">{center?.mobile}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <Label className="text-slate-500 text-xs">Address</Label>
                    <p className="text-slate-800">{center?.address}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <Label className="text-slate-500 text-xs">City</Label>
                    <p className="text-slate-800">{center?.city}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <Label className="text-slate-500 text-xs">District</Label>
                    <p className="text-slate-800">{center?.district}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <Label className="text-slate-500 text-xs">State</Label>
                    <p className="text-slate-800">{center?.state}</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                    <Label className="text-emerald-600 text-xs">Your Revenue Share</Label>
                    <p className="text-emerald-700 font-bold text-lg">{100 - (center?.commission_rate || 40)}%</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CenterDashboard;
